"use client";

import { useCallback, useMemo, useState } from "react";
import {
  type BriefGroup,
  groupBriefsByCategory,
} from "../../_lib/brief-grouping";
import type { ExploreBriefCard } from "../../types";
import { SectionHeader } from "../section-header";
import { BriefPreviewDialog } from "./brief-preview-dialog";
import { BriefStoriesVerticalOverlay } from "./briefs-stories-vertical-overlay";
import { PlayAllButton } from "./play-all-button";
import { useBriefsOverlay } from "./use-briefs-overlay";

const MAX_VISIBLE = 3;

// ─── Flat brief row — title + description + tags right ───────

function FlatBriefRow({
  brief,
  onClick,
}: {
  brief: ExploreBriefCard;
  onClick: () => void;
}) {
  return (
    <button
      className="flex w-full gap-4 border-[var(--explore-border-light)] border-b py-3.5 text-left transition-colors last:border-b-0 hover:bg-[var(--explore-bg-highlight)]"
      onClick={onClick}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
      type="button"
    >
      {/* Left: content */}
      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex items-center gap-2">
          <span className="text-[0.65rem] text-[var(--explore-text-muted)] tabular-nums">
            {brief.timeAgo}
          </span>
        </div>
        <p className="font-semibold text-[0.88rem] text-[var(--explore-text-primary)] leading-[1.4]">
          {brief.title}
        </p>
        {brief.tldr && (
          <p className="mt-1 line-clamp-1 text-[0.78rem] text-[var(--explore-text-secondary)] leading-[1.45]">
            {brief.tldr}
          </p>
        )}
      </div>

      {/* Right: tags */}
      {brief.tags.length > 0 && (
        <div className="hidden shrink-0 flex-col items-end gap-1 pt-4 sm:flex">
          {brief.tags.slice(0, 2).map((tag) => (
            <span
              className="rounded-full bg-[var(--explore-tag-bg)] px-2 py-0.5 text-[0.65rem] text-[var(--explore-tag-text)]"
              key={tag.slug}
            >
              {tag.displayName}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

// ─── Category group — uppercase header, NO chevrons, NO bg ───

function CategoryGroup({
  group,
  onBriefClick,
}: {
  group: BriefGroup;
  onBriefClick: (brief: ExploreBriefCard) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasMore = group.briefs.length > MAX_VISIBLE;
  const remainingCount = group.briefs.length - MAX_VISIBLE;
  const visibleBriefs = isExpanded
    ? group.briefs
    : group.briefs.slice(0, MAX_VISIBLE);

  return (
    <div>
      {/* Uppercase category header with count on right */}
      <div
        className="mb-1 flex items-center justify-between px-1 lg:px-0"
        style={{ fontFamily: "var(--homepage-sans-stack)" }}
      >
        <div className="flex items-center gap-2">
          <span className="text-[0.78rem]">{group.categoryEmoji}</span>
          <span className="font-bold text-[0.7rem] text-[var(--explore-text-secondary)] uppercase tracking-[0.08em]">
            {group.categoryDisplayName}
          </span>
        </div>
        <span className="text-[0.68rem] text-[var(--explore-text-muted)]">
          {group.briefs.length} briefs
        </span>
      </div>

      {/* Flat list — no card background */}
      <div className="px-1 lg:px-0">
        {visibleBriefs.map((brief) => (
          <FlatBriefRow
            brief={brief}
            key={brief.briefId}
            onClick={() => onBriefClick(brief)}
          />
        ))}

        {/* Show more */}
        {hasMore && !isExpanded && (
          <button
            className="mt-1 mb-2 w-full py-2 text-center text-[0.75rem] text-[var(--explore-text-muted)] transition-colors hover:text-[var(--explore-text-primary)]"
            onClick={() => setIsExpanded(true)}
            style={{ fontFamily: "var(--homepage-sans-stack)" }}
            type="button"
          >
            + {remainingCount} more
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Export ──────────────────────────────────────────────────

interface BriefsFlatSectionProps {
  briefs: ExploreBriefCard[];
  briefsReportDate: string;
  exploreBase: string;
  className?: string;
}

export function BriefsFlatSection({
  briefs,
  briefsReportDate,
  className,
}: BriefsFlatSectionProps) {
  const {
    selectedBrief,
    dialogOpen,
    setDialogOpen,
    storiesOpen,
    handleBriefClick,
    handlePlayAll,
    handleCloseStories,
    allViewed,
    handleAllViewed,
  } = useBriefsOverlay();

  const groups = useMemo(() => groupBriefsByCategory({ briefs }), [briefs]);
  const categoryBriefs = useMemo(() => {
    if (!selectedBrief) {
      return;
    }
    const group = groups.find(
      (candidate) =>
        candidate.categorySlug ===
        (selectedBrief.categorySlug ?? "uncategorized")
    );
    return group?.briefs;
  }, [groups, selectedBrief]);
  const handleNavigateBrief = useCallback(
    (brief: ExploreBriefCard) => {
      handleBriefClick(brief);
    },
    [handleBriefClick]
  );

  if (briefs.length === 0) {
    return null;
  }

  const dateLabel = formatBriefsDate({ reportDate: briefsReportDate });

  return (
    <section className={className}>
      <SectionHeader subtitle={dateLabel} title="Briefs">
        <PlayAllButton
          allViewed={allViewed}
          onPlay={handlePlayAll}
          totalBriefs={briefs.length}
        />
      </SectionHeader>

      <div className="flex flex-col gap-6 px-1 lg:px-0">
        {groups.map((group) => (
          <CategoryGroup
            group={group}
            key={group.categorySlug}
            onBriefClick={handleBriefClick}
          />
        ))}
      </div>

      <BriefPreviewDialog
        brief={selectedBrief}
        categoryBriefs={categoryBriefs}
        onNavigateBrief={handleNavigateBrief}
        onOpenChange={setDialogOpen}
        open={dialogOpen}
      />

      {storiesOpen && (
        <BriefStoriesVerticalOverlay
          briefs={briefs}
          onAllViewed={handleAllViewed}
          onClose={handleCloseStories}
        />
      )}
    </section>
  );
}

function formatBriefsDate({ reportDate }: { reportDate: string }): string {
  const [year, month, day] = reportDate.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-US", { month: "long", day: "numeric" });
}
